import {
  ImpactType,
  User,
  Wordpress,
  UserImpactAggregates,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { HowCalculatedModalTrees } from "../HowCalculatedModal";
import { ImpactStatCard } from "../ImpactStatCard";
import { EmptyImpactBanner } from "../EmptyImpactBanner";
import { HowCalculatedModalImpact } from "../HowCalculatedModal/HowCalculatedModalImpact";

const StatsContainer = styled.div<{ itemCount: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: 34px 14px;
  justify-content: center;
  margin: auto;
  ${(props) => props.itemCount === 4 && "width: 242px;"}

  @media ${({ theme }) => theme.bp.extraSmallUp} {
    gap: 52px 24px;
    ${(props) => props.itemCount === 4 && "width: 380px;"}
  }

  @media ${({ theme }) => theme.bp.desktopUp} {
    width: 100%;
  }
`;

const LoadingContainer = styled.div`
  height: 306px;
  @media ${({ theme }) => theme.bp.tabletUp} {
    height: 380px;
  }
  @media ${({ theme }) => theme.bp.desktopUp} {
    height: 164px;
  }
`;

export type CombinedImpactQuantities = {
  paid: number;
  pending: number;
};

export const ImpactStats = ({
  allProjects,
  isOwnProfile,
  impactTotals,
  user,
  userImpactAggregates,
  isLoading,
}: {
  allProjects: Wordpress.Project[];
  isOwnProfile: boolean;
  impactTotals: Record<ImpactType, CombinedImpactQuantities>;
  user: User;
  userImpactAggregates: UserImpactAggregates;
  isLoading: boolean;
}) => {
  const isImpactTypeVisible = (type: ImpactType) =>
    isOwnProfile ||
    impactTotals[type].paid > 0 ||
    impactTotals[type].pending > 0;

  const isTreesVisible = isImpactTypeVisible(ImpactType.TREES);
  const isAvoidanceVisible = isImpactTypeVisible(ImpactType.CARBON_OFFSETS);
  const isRemovalVisible = isImpactTypeVisible(ImpactType.CARBON_REMOVAL);
  const isRestorationVisible = isImpactTypeVisible(
    ImpactType.HABITAT_RESTORATION
  );

  const isStatsVisible =
    isTreesVisible ||
    isAvoidanceVisible ||
    isRemovalVisible ||
    isRestorationVisible;

  const [modalState, setModalState] =
    useState<Exclude<ImpactType, ImpactType.TREES>>();
  const [isTreesModalOpen, setIsTreesModalOpen] = useState(false);

  const numberOfStats = [
    isTreesVisible,
    isAvoidanceVisible,
    isRemovalVisible,
    isRestorationVisible,
  ].filter(Boolean).length;

  return (
    <>
      {isLoading ? (
        <LoadingContainer data-testid="impact-stats-loading-spacer" />
      ) : (
        <>
          {isStatsVisible ? (
            <StatsContainer itemCount={numberOfStats}>
              {isTreesVisible && (
                <ImpactStatCard
                  isLarge={numberOfStats === 1}
                  impactType={ImpactType.TREES}
                  quantities={impactTotals[ImpactType.TREES]}
                  onClick={() => setIsTreesModalOpen(true)}
                  aria-label={"Open tree project breakdown"}
                />
              )}
              {isAvoidanceVisible && (
                <ImpactStatCard
                  isLarge={numberOfStats === 1}
                  impactType={ImpactType.CARBON_OFFSETS}
                  quantities={impactTotals[ImpactType.CARBON_OFFSETS]}
                  onClick={() => setModalState(ImpactType.CARBON_OFFSETS)}
                  aria-label={"Open carbon avoidance project breakdown"}
                />
              )}
              {isRemovalVisible && (
                <ImpactStatCard
                  isLarge={numberOfStats === 1}
                  impactType={ImpactType.CARBON_REMOVAL}
                  quantities={impactTotals[ImpactType.CARBON_REMOVAL]}
                  onClick={() => setModalState(ImpactType.CARBON_REMOVAL)}
                  aria-label={"Open carbon removal project breakdown"}
                />
              )}
              {isRestorationVisible && (
                <ImpactStatCard
                  isLarge={numberOfStats === 1}
                  impactType={ImpactType.HABITAT_RESTORATION}
                  quantities={impactTotals[ImpactType.HABITAT_RESTORATION]}
                  onClick={() => setModalState(ImpactType.HABITAT_RESTORATION)}
                  aria-label={"Open habitat restoration project breakdown"}
                />
              )}
            </StatsContainer>
          ) : (
            <EmptyImpactBanner />
          )}
        </>
      )}
      {modalState && (
        <HowCalculatedModalImpact
          isOpen={!!modalState}
          impactType={modalState}
          pendingTotal={impactTotals[modalState].pending}
          aggregateOfUserImpact={userImpactAggregates[modalState]}
          onClose={() => setModalState(undefined)}
          user={user}
        />
      )}
      <HowCalculatedModalTrees
        pendingTotal={impactTotals[ImpactType.TREES].pending}
        isOpen={isTreesModalOpen}
        onClose={() => setIsTreesModalOpen(false)}
        projectsIncludingTrees={allProjects}
        user={user}
      />
    </>
  );
};
